<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

const { t } = useI18n();
const { localeTag } = useDateLocale();

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({
      active_flows: 0,
      delivered_messages: { total: 0, recent: 0 },
      orders_revenue: { total: '0', recent: '0' },
      orders_count: { total: 0, recent: 0 },
      window_days: 30,
    }),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const currencyFormatter = computed(() =>
  new Intl.NumberFormat(localeTag.value, { style: 'currency', currency: 'BRL' })
);
const numberFormatter = computed(() => new Intl.NumberFormat(localeTag.value));

const formatNumber = v => numberFormatter.value.format(Number(v) || 0);
const formatCurrency = v => currencyFormatter.value.format(Number(v) || 0);

const windowDays = computed(() => props.metrics?.window_days ?? 30);

const cards = computed(() => [
  {
    key: 'active_flows',
    label: t('FLOW_BUILDER.METRICS.ACTIVE_FLOWS'),
    value: formatNumber(props.metrics?.active_flows),
    badge: null,
  },
  {
    key: 'delivered_messages',
    label: t('FLOW_BUILDER.METRICS.DELIVERED_MESSAGES'),
    value: formatNumber(props.metrics?.delivered_messages?.total),
    badge: formatNumber(props.metrics?.delivered_messages?.recent),
  },
  {
    key: 'orders_revenue',
    label: t('FLOW_BUILDER.METRICS.REVENUE'),
    value: formatCurrency(props.metrics?.orders_revenue?.total),
    badge: formatCurrency(props.metrics?.orders_revenue?.recent),
  },
  {
    key: 'orders_count',
    label: t('FLOW_BUILDER.METRICS.ORDERS'),
    value: formatNumber(props.metrics?.orders_count?.total),
    badge: formatNumber(props.metrics?.orders_count?.recent),
  },
]);
</script>

<template>
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
    <div
      v-for="card in cards"
      :key="card.key"
      class="flex flex-col gap-1.5 border rounded-lg border-n-slate-4 bg-n-solid-1 p-4"
    >
      <span
        class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
      >
        {{ card.label }}
      </span>
      <span
        v-if="loading"
        class="h-7 w-24 rounded bg-n-alpha-2 animate-pulse"
      />
      <span v-else class="text-2xl font-semibold text-n-slate-12 tabular-nums">
        {{ card.value }}
      </span>
      <div
        v-if="!loading && card.badge !== null"
        class="flex items-center gap-1.5 text-[10px] text-n-slate-10"
      >
        <span
          class="inline-flex items-center px-2 py-0.5 rounded-full bg-n-alpha-2 font-medium text-n-slate-11 tabular-nums"
        >
          {{ card.badge }}
        </span>
        {{ t('FLOW_BUILDER.METRICS.LAST_DAYS', { days: windowDays }) }}
      </div>
    </div>
  </div>
</template>
